import dayjs from 'dayjs';
import {
    BatchPushAreaRow,
    BatchPushBoard,
    BatchPushCellInput,
    BatchPushColumn,
    BatchPushPreviewCell,
    RETURN_REDISPATCH_BATCH,
} from './graphql/batchPush';

/**
 * 批次推送页的纯函数：勾选状态、表头三态、已选汇总、列头文案、区域搜索。
 * 勾选状态用单元格 key 的 Set 表示，只有件数大于 0 的单元格可勾选。
 */

/** 表头勾选三态：0-未选 1-部分 2-全选 */
export type TriState = 0 | 1 | 2;

/** 单元格 key：批次号 + 区域 ID */
export const cellKey = (wmsBatchNumber: string, areaId: number): string => `${wmsBatchNumber}|${areaId}`;

/** 解析单元格 key */
export const parseCellKey = (key: string): { wmsBatchNumber: string; areaId: number } => {
    const idx = key.lastIndexOf('|');
    return { wmsBatchNumber: key.slice(0, idx), areaId: Number(key.slice(idx + 1)) };
};

/** 单元格件数索引：key → 件数（只收录件数大于 0 的单元格） */
export const buildCellCountMap = (board: BatchPushBoard | undefined | null): Map<string, number> => {
    const map = new Map<string, number>();
    (board?.cells ?? []).forEach((c) => {
        if (c.wmsBatchNumber == null || c.areaId == null) return;
        const count = Number(c.count ?? 0);
        if (count > 0) {
            map.set(cellKey(c.wmsBatchNumber, Number(c.areaId)), count);
        }
    });
    return map;
};

/** 某列（批次）下所有可勾选单元格 */
export const columnKeys = (counts: Map<string, number>, wmsBatchNumber: string): string[] =>
    Array.from(counts.keys()).filter((k) => parseCellKey(k).wmsBatchNumber === wmsBatchNumber);

/** 若干行（区域）下所有可勾选单元格 */
export const rowsKeys = (counts: Map<string, number>, areaIds: number[]): string[] => {
    const ids = new Set(areaIds);
    return Array.from(counts.keys()).filter((k) => ids.has(parseCellKey(k).areaId));
};

/** 一组单元格的勾选三态 */
export const triState = (keys: string[], selected: Set<string>): TriState => {
    if (keys.length === 0) return 0;
    const n = keys.filter((k) => selected.has(k)).length;
    if (n === 0) return 0;
    return n === keys.length ? 2 : 1;
};

/**
 * 整体切换：一个都没选时全部勾上；全选或部分选中时全部取消。
 * 部分选中时取消是为了支持 PRD 的行列交叉：先全选几列再点某行即「剔除该行」，而不是把该行其他批次也勾上。
 */
export const toggleKeys = (keys: string[], selected: Set<string>): Set<string> => {
    const next = new Set(selected);
    const anyOn = keys.some((k) => next.has(k));
    keys.forEach((k) => (anyOn ? next.delete(k) : next.add(k)));
    return next;
};

/** 全选框：未全选时全部勾上，已全选时全部取消（PRD「全选操作：选中所有格子」） */
export const toggleAllKeys = (keys: string[], selected: Set<string>): Set<string> => {
    const allOn = keys.length > 0 && keys.every((k) => selected.has(k));
    return allOn ? new Set<string>() : new Set(keys);
};

/** 看板刷新后剔除已不存在的单元格，保留仍可推的勾选 */
export const pruneSelection = (selected: Set<string>, counts: Map<string, number>): Set<string> =>
    new Set(Array.from(selected).filter((k) => counts.has(k)));

/** 一组单元格的件数合计 */
export const sumCounts = (keys: Iterable<string>, counts: Map<string, number>): number => {
    let total = 0;
    for (const k of Array.from(keys)) total += counts.get(k) ?? 0;
    return total;
};

/** 区域分组键：区域名首字母（大写），缺失归「#」 */
export const areaGroupKey = (areaName: string | null | undefined): string => {
    const first = (areaName ?? '').trim().charAt(0);
    return first ? first.toUpperCase() : '#';
};

/** 区域按首字母分组，保持后端给出的顺序（后端已按「字母 + 数字」排序） */
export const groupAreas = (areas: BatchPushAreaRow[]): Array<{ group: string; areas: BatchPushAreaRow[] }> => {
    const groups: Array<{ group: string; areas: BatchPushAreaRow[] }> = [];
    const index = new Map<string, number>();
    areas.forEach((a) => {
        const g = areaGroupKey(a.areaName);
        if (!index.has(g)) {
            index.set(g, groups.length);
            groups.push({ group: g, areas: [] });
        }
        groups[index.get(g)!].areas.push(a);
    });
    return groups;
};

/** 区域搜索：按空格、逗号拆成多个关键字，区域名包含任一关键字即命中（不区分大小写） */
export const matchAreas = (areas: BatchPushAreaRow[], query: string): number[] => {
    const words = query.split(/[\s,，、]+/).map((w) => w.trim().toLowerCase()).filter(Boolean);
    if (words.length === 0) return [];
    return areas
        .filter((a) => a.areaId != null && words.some((w) => (a.areaName ?? '').toLowerCase().includes(w)))
        .map((a) => Number(a.areaId));
};

/** 已选明细中的一个批次 */
export interface SelectedBatchSummary {
    column: BatchPushColumn;
    count: number;
    areaNames: string[];
}

/** 已选汇总：总件数、回仓重派、新到仓，以及按列顺序的批次明细（区域按行顺序） */
export interface SelectionSummary {
    total: number;
    returnRedispatch: number;
    newArrival: number;
    batches: SelectedBatchSummary[];
}

export const summarizeSelection = (
    board: BatchPushBoard | undefined | null,
    selected: Set<string>,
    counts: Map<string, number>,
): SelectionSummary => {
    const areaOrder = (board?.areas ?? []).filter((a) => a.areaId != null);
    const batches: SelectedBatchSummary[] = [];
    let total = 0;
    let returnRedispatch = 0;
    (board?.batches ?? []).forEach((col) => {
        if (!col.wmsBatchNumber) return;
        const batch = col.wmsBatchNumber;
        const areas = areaOrder.filter((a) => selected.has(cellKey(batch, Number(a.areaId))));
        if (areas.length === 0) return;
        const count = sumCounts(areas.map((a) => cellKey(batch, Number(a.areaId))), counts);
        total += count;
        if (batch === RETURN_REDISPATCH_BATCH) returnRedispatch += count;
        batches.push({ column: col, count, areaNames: areas.map((a) => a.areaName ?? String(a.areaId)) });
    });
    return { total, returnRedispatch, newArrival: total - returnRedispatch, batches };
};

/** 已选单元格 → 提交 / 预览入参（selectedCount 取当前看板件数） */
export const toCellInputs = (selected: Set<string>, counts: Map<string, number>): BatchPushCellInput[] =>
    Array.from(selected)
        .filter((k) => counts.has(k))
        .map((k) => {
            const { wmsBatchNumber, areaId } = parseCellKey(k);
            return { wmsBatchNumber, areaId, selectedCount: counts.get(k) ?? 0 };
        });

/** 数字千分位（PRD 示例：13,485） */
export const formatNumber = (value: number | null | undefined): string => Number(value ?? 0).toLocaleString('en-US');

/** 确认推送前置比对：某一方向（增加 / 剔除）的变化汇总 */
export interface PreviewChangeSide {
    total: number;
    /** 涉及区域，按看板行顺序（「字母 + 数字」） */
    areas: Array<{ areaId: number; areaName: string; count: number }>;
}

/**
 * 汇总确认推送前置比对的变化（PRD「统计文案第二行」）：逐格比较勾选时与当前件数，
 * 当前多出的计入「增加」、减少的计入「剔除」，各自按区域汇总。同一区域可能在一个批次增加、另一个批次剔除，两边都会出现。
 *
 * @param cells 预览返回的单元格比对明细
 * @param areas 看板区域（用于区域名与排序）
 */
export const summarizePreviewChanges = (
    cells: BatchPushPreviewCell[] | null | undefined,
    areas: BatchPushAreaRow[] | null | undefined,
): { added: PreviewChangeSide; removed: PreviewChangeSide } => {
    const addedByArea = new Map<number, number>();
    const removedByArea = new Map<number, number>();
    (cells ?? []).forEach((c) => {
        if (c.areaId == null) return;
        const areaId = Number(c.areaId);
        const diff = Number(c.currentCount ?? 0) - Number(c.selectedCount ?? 0);
        if (diff > 0) addedByArea.set(areaId, (addedByArea.get(areaId) ?? 0) + diff);
        if (diff < 0) removedByArea.set(areaId, (removedByArea.get(areaId) ?? 0) - diff);
    });
    const order = (areas ?? []).filter((a) => a.areaId != null);
    const names = new Map(order.map((a) => [Number(a.areaId), a.areaName ?? String(a.areaId)]));
    const rank = new Map(order.map((a, i) => [Number(a.areaId), i]));
    const toSide = (byArea: Map<number, number>): PreviewChangeSide => {
        const list = Array.from(byArea.entries())
            .map(([areaId, count]) => ({ areaId, areaName: names.get(areaId) ?? String(areaId), count }))
            .sort((x, y) => (rank.get(x.areaId) ?? Number.MAX_SAFE_INTEGER) - (rank.get(y.areaId) ?? Number.MAX_SAFE_INTEGER));
        return { total: list.reduce((sum, a) => sum + a.count, 0), areas: list };
    };
    return { added: toSide(addedByArea), removed: toSide(removedByArea) };
};

/** 列头节点 → i18n key */
export const displayNodeLabelKey = (displayNode: string | null | undefined): string => {
    switch (displayNode) {
        case 'HUB_DEPARTURE':
            return 'wms.batchPush.node.hubDeparture';
        case 'WHS_ARRIVAL':
            return 'wms.batchPush.node.whsArrival';
        case 'BATCH_CREATED':
            return 'wms.batchPush.node.batchCreated';
        case 'RETURN_REDISPATCH':
            return 'wms.batchPush.node.returnRedispatch';
        case 'DEFAULT':
            return 'wms.batchPush.node.defaultBatch';
        default:
            return 'wms.batchPush.node.unknown';
    }
};

/**
 * 批次时间：统一显示日期 + 时间「M/D HH:mm」（如 11/1 01:03、10/30 23:53），不再显示「今天 / 昨天」。
 * displayTime 为仓库当地时间（后端已按仓库时区转换），原样格式化。
 *
 * @return 格式化文本；为空或无效返回 null
 */
export const formatColumnTime = (displayTime: string | null | undefined): string | null => {
    if (!displayTime) return null;
    const time = dayjs(displayTime);
    return time.isValid() ? time.format('M/D HH:mm') : null;
};

/** 生成提交幂等号：优先使用浏览器 UUID，不支持时退化为时间戳 + 随机数 */
export const newRequestNo = (): string => {
    const c = (globalThis as any).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};
